// =============================================================================
// GOUV RULES - Widget Gouvernance : Policy Engine Transversal
// Créé le 22/02/2026 - MAJ 23/02/2026 (support multi-catégories)
// UI pour gérer les règles multi-domaines (TECH, BIZ, SEC, DEPLOY)
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Scale,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  History,
  RotateCcw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Filter,
  Search,
  Code,
  Briefcase,
  Lock,
  Rocket,
  Info,
  Tag,
  Layers
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par sévérité
const SEVERITY_COLORS = {
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-blue-500/20 text-blue-400 border-blue-500/30"
};

const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical", color: "text-red-400" },
  { value: "high", label: "High", color: "text-orange-400" },
  { value: "medium", label: "Medium", color: "text-yellow-400" },
  { value: "low", label: "Low", color: "text-blue-400" }
];

// Configuration des catégories
const CATEGORIES = [
  { 
    id: "all", 
    label: "Toutes", 
    icon: Layers, 
    color: "text-zinc-400",
    bgColor: "bg-zinc-500/10",
    description: "Toutes les règles"
  },
  { 
    id: "technical", 
    label: "Technique", 
    icon: Code, 
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    description: "Scan architecture (Groot)",
    status: "active"
  },
  { 
    id: "business", 
    label: "Métier", 
    icon: Briefcase, 
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    description: "Workflows & validations",
    status: "phase_2",
    badge: "Phase 2"
  },
  { 
    id: "security", 
    label: "Sécurité", 
    icon: Lock, 
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    description: "RBAC dynamique",
    status: "phase_2",
    badge: "Phase 2"
  },
  { 
    id: "deploy", 
    label: "Déploiement", 
    icon: Rocket, 
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    description: "Gates CI/CD",
    status: "phase_2",
    badge: "Phase 2"
  }
];

export default function GouvRules() {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [expandedRule, setExpandedRule] = useState(null);
  const [historyDialog, setHistoryDialog] = useState({ open: false, rule: null, history: [] });
  const [updatingRule, setUpdatingRule] = useState(null);

  // Charger les données
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Charger stats et règles en parallèle
      const categoryParam = categoryFilter !== "all" ? `?category=${categoryFilter}` : "";
      const [statsRes, rulesRes] = await Promise.all([
        fetch(`${API}/api/policy/rules/stats`, { headers }),
        fetch(`${API}/api/policy/rules${categoryParam}`, { headers })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }

      if (rulesRes.ok) {
        const rulesData = await rulesRes.json();
        setRules(rulesData.rules || []);
      }
    } catch (err) {
      console.error("Erreur chargement règles:", err);
      toast.error("Erreur de chargement des règles");
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle une règle
  const toggleRule = async (code, enabled) => {
    setUpdatingRule(code);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/policy/rules/${code}/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ enabled })
      });

      if (res.ok) {
        toast.success(`Règle ${code} ${enabled ? "activée" : "désactivée"}`);
        loadData();
      } else {
        toast.error("Erreur lors de la modification");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setUpdatingRule(null);
    }
  };

  // Changer la sévérité
  const changeSeverity = async (code, severity) => {
    setUpdatingRule(code);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/policy/rules/${code}/severity`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ severity })
      });

      if (res.ok) {
        toast.success(`Sévérité de ${code} changée en ${severity}`);
        loadData();
      } else {
        toast.error("Erreur lors de la modification");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setUpdatingRule(null);
    }
  };

  // Reset une règle
  const resetRule = async (code) => {
    setUpdatingRule(code);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/policy/rules/${code}/reset`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        toast.success(`Règle ${code} réinitialisée`);
        loadData();
      } else {
        toast.error("Erreur lors de la réinitialisation");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setUpdatingRule(null);
    }
  };

  // Charger l'historique
  const loadHistory = async (rule) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/policy/rules/${rule.code}/history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        const data = await res.json();
        setHistoryDialog({ open: true, rule, history: data.history || [] });
      }
    } catch (err) {
      toast.error("Erreur chargement historique");
    }
  };

  // Filtrer les règles
  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rule.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === "all" || rule.severity === severityFilter;
    const matchesStatus = statusFilter === "all" ||
                         (statusFilter === "enabled" && rule.enabled) ||
                         (statusFilter === "disabled" && !rule.enabled);
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  // Obtenir l'icône de catégorie
  const getCategoryIcon = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat?.icon || Code;
  };

  const getCategoryColor = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat?.color || "text-zinc-400";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Scale className="w-7 h-7 text-amber-400" />
            Policy Engine
            <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
              Transversal
            </Badge>
          </h2>
          <p className="text-zinc-500 mt-1">Moteur de règles unifié - TECH → BIZ</p>
        </div>
        <div className="flex items-center gap-2">
          {stats?.cache_hash && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-500">
                    Cache: {stats.cache_hash}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Hash du cache des règles</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="border-zinc-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats globales */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
            <div className="text-xl font-bold text-white">{stats.total}</div>
            <div className="text-xs text-zinc-500">Total</div>
          </div>
          <div className="bg-zinc-900/50 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-xl font-bold text-emerald-400">{stats.enabled}</div>
            <div className="text-xs text-zinc-500">Actives</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-700 rounded-lg p-3">
            <div className="text-xl font-bold text-zinc-400">{stats.disabled}</div>
            <div className="text-xs text-zinc-500">Désactivées</div>
          </div>
          <div className="bg-zinc-900/50 border border-red-500/30 rounded-lg p-3">
            <div className="text-xl font-bold text-red-400">{stats.by_severity?.critical || 0}</div>
            <div className="text-xs text-zinc-500">Critical</div>
          </div>
          <div className="bg-zinc-900/50 border border-orange-500/30 rounded-lg p-3">
            <div className="text-xl font-bold text-orange-400">{stats.by_severity?.high || 0}</div>
            <div className="text-xs text-zinc-500">High</div>
          </div>
          <div className="bg-zinc-900/50 border border-yellow-500/30 rounded-lg p-3">
            <div className="text-xl font-bold text-yellow-400">{stats.by_severity?.medium || 0}</div>
            <div className="text-xs text-zinc-500">Medium</div>
          </div>
        </div>
      )}

      {/* Tabs par catégorie */}
      <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="w-full">
        <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1 h-auto flex-wrap">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const catStats = stats?.by_category?.[cat.id];
            return (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                className={`flex items-center gap-2 data-[state=active]:${cat.bgColor} data-[state=active]:${cat.color}`}
                data-testid={`category-tab-${cat.id}`}
              >
                <Icon className={`w-4 h-4 ${categoryFilter === cat.id ? cat.color : 'text-zinc-500'}`} />
                <span>{cat.label}</span>
                {cat.id !== "all" && catStats && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1">
                    {catStats.enabled}/{catStats.total}
                  </Badge>
                )}
                {cat.badge && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 border-zinc-600 text-zinc-500">
                    {cat.badge}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Info catégorie active */}
        {categoryFilter !== "all" && (
          <div className="mt-3 flex items-center gap-2 text-sm text-zinc-500">
            <Info className="w-4 h-4" />
            {CATEGORIES.find(c => c.id === categoryFilter)?.description}
          </div>
        )}
      </Tabs>

      {/* Filtres */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Rechercher une règle..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-700"
            data-testid="rules-search"
          />
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-full max-w-[140px] bg-zinc-900/50 border-zinc-700" data-testid="severity-filter">
            <SelectValue placeholder="Sévérité" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full max-w-[140px] bg-zinc-900/50 border-zinc-700" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="enabled">Actives</SelectItem>
            <SelectItem value="disabled">Désactivées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Liste des règles */}
      <div className="space-y-3">
        {filteredRules.map(rule => {
          const CategoryIcon = getCategoryIcon(rule.category);
          return (
            <div
              key={rule.code}
              className={`bg-zinc-900/50 border rounded-lg overflow-hidden transition-all ${
                rule.enabled ? "border-zinc-800" : "border-zinc-800/50 opacity-60"
              }`}
              data-testid={`rule-${rule.code}`}
            >
              {/* Header de la règle */}
              <div className="p-4 flex items-center gap-4">
                {/* Toggle */}
                <Switch
                  checked={rule.enabled}
                  onCheckedChange={(checked) => toggleRule(rule.code, checked)}
                  disabled={updatingRule === rule.code}
                  className="data-[state=checked]:bg-emerald-500"
                  data-testid={`toggle-${rule.code}`}
                />

                {/* Code et description */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Icône de catégorie */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <CategoryIcon className={`w-4 h-4 ${getCategoryColor(rule.category)}`} />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{CATEGORIES.find(c => c.id === rule.category)?.label || rule.category}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <span className="font-mono font-bold text-white">{rule.code}</span>
                    <Badge className={SEVERITY_COLORS[rule.severity]}>
                      {rule.severity}
                    </Badge>
                    {rule.subcategory && (
                      <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                        {rule.subcategory}
                      </Badge>
                    )}
                    {rule.original_severity && rule.severity !== rule.original_severity && (
                      <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                        était {rule.original_severity}
                      </Badge>
                    )}
                    {rule.version > 1 && (
                      <Badge variant="outline" className="text-xs border-zinc-600">
                        v{rule.version}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400 truncate mt-1">{rule.description}</p>
                  {/* Tags */}
                  {rule.tags?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Tag className="w-3 h-3 text-zinc-600" />
                      {rule.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-[10px] text-zinc-600">
                          {tag}{i < Math.min(rule.tags.length, 3) - 1 && ","}
                        </span>
                      ))}
                      {rule.tags.length > 3 && (
                        <span className="text-[10px] text-zinc-600">+{rule.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {/* Sélecteur de sévérité */}
                  <Select
                    value={rule.severity}
                    onValueChange={(value) => changeSeverity(rule.code, value)}
                    disabled={updatingRule === rule.code}
                  >
                    <SelectTrigger className="w-full max-w-[100px] h-8 text-xs bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className={opt.color}>{opt.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Historique */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => loadHistory(rule)}
                    title="Historique"
                  >
                    <History className="w-4 h-4 text-zinc-500" />
                  </Button>

                  {/* Reset */}
                  {(rule.original_severity && rule.severity !== rule.original_severity) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => resetRule(rule.code)}
                      disabled={updatingRule === rule.code}
                      title="Réinitialiser"
                    >
                      <RotateCcw className="w-4 h-4 text-amber-400" />
                    </Button>
                  )}

                  {/* Expand */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setExpandedRule(expandedRule === rule.code ? null : rule.code)}
                  >
                    {expandedRule === rule.code ? (
                      <ChevronUp className="w-4 h-4 text-zinc-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Détails expandés */}
              {expandedRule === rule.code && (
                <div className="px-4 pb-4 pt-0 border-t border-zinc-800 bg-zinc-950/50">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-sm">
                    <div>
                      <span className="text-zinc-500">Pattern interdit:</span>
                      <code className="block mt-1 p-2 bg-zinc-900 rounded text-xs text-red-400 overflow-x-auto">
                        {rule.pattern_forbidden || "—"}
                      </code>
                    </div>
                    <div>
                      <span className="text-zinc-500">Pattern requis:</span>
                      <code className="block mt-1 p-2 bg-zinc-900 rounded text-xs text-emerald-400 overflow-x-auto">
                        {rule.pattern_required || "—"}
                      </code>
                    </div>
                    <div className="col-span-2">
                      <span className="text-zinc-500">Fichiers ciblés:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rule.file_patterns?.map((pattern, i) => (
                          <Badge key={i} variant="outline" className="text-xs border-zinc-700">
                            {pattern}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {rule.context && (
                      <div className="col-span-2">
                        <span className="text-zinc-500">Contexte:</span>
                        <p className="mt-1 text-zinc-400">{rule.context}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredRules.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Scale className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Aucune règle trouvée</p>
            {categoryFilter !== "all" && categoryFilter !== "technical" && (
              <p className="text-xs mt-2">
                Les règles {CATEGORIES.find(c => c.id === categoryFilter)?.label} seront disponibles en Phase 2
              </p>
            )}
          </div>
        )}
      </div>

      {/* Dialog Historique */}
      <Dialog open={historyDialog.open} onOpenChange={(open) => setHistoryDialog({ ...historyDialog, open })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-400" />
              Historique de {historyDialog.rule?.code}
            </DialogTitle>
            <DialogDescription>
              Modifications apportées à cette règle
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {historyDialog.history.length > 0 ? (
              historyDialog.history.map((entry, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <div className={`p-1.5 rounded ${
                    entry.action === "toggle" ? "bg-blue-500/20" :
                    entry.action === "severity_change" ? "bg-amber-500/20" :
                    entry.action === "category_change" ? "bg-purple-500/20" :
                    "bg-zinc-500/20"
                  }`}>
                    {entry.action === "toggle" ? (
                      entry.new_value === "enabled" ? 
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" /> :
                        <XCircle className="w-4 h-4 text-red-400" />
                    ) : entry.action === "severity_change" ? (
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                    ) : entry.action === "category_change" ? (
                      <Layers className="w-4 h-4 text-purple-400" />
                    ) : (
                      <RotateCcw className="w-4 h-4 text-zinc-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-white">
                      {entry.action === "toggle" && `${entry.new_value === "enabled" ? "Activée" : "Désactivée"}`}
                      {entry.action === "severity_change" && `Sévérité: ${entry.old_value} → ${entry.new_value}`}
                      {entry.action === "category_change" && `Catégorie: ${entry.old_value} → ${entry.new_value}`}
                      {entry.action === "reset" && `Réinitialisée à ${entry.new_value}`}
                      {entry.action === "version_prepared" && `Version ${entry.new_value} préparée`}
                      {entry.action === "version_approved" && `Version ${entry.new_value} approuvée`}
                    </div>
                    {entry.version && (
                      <Badge variant="outline" className="text-[10px] mt-1 border-zinc-600">
                        v{entry.version}
                      </Badge>
                    )}
                    <div className="text-xs text-zinc-500 mt-1">
                      Par {entry.modified_by} • {new Date(entry.modified_at).toLocaleString('fr-FR')}
                    </div>
                    {entry.reason && (
                      <div className="text-xs text-zinc-400 mt-1 italic">
                        "{entry.reason}"
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-zinc-500">
                Aucune modification enregistrée
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
